import { ToolCall } from "@cohesive/api-client";
import { Markdown } from "@cohesive/ui-components";
import React from "react";
import { titleCase } from "title-case";

// Codex tool calls carry no Cohesive toolSpec, so `arguments` reaches the
// confirmation UI absent (no-arg calls omit the field) or as a JSON string.
function normalizeArgs(
  args: Record<string, any> | string | null | undefined,
): Record<string, any> | string {
  if (args == null) {
    return {};
  }

  if (typeof args !== "string") {
    return args;
  }

  try {
    const parsed = JSON.parse(args);
    return typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? parsed
      : args;
  } catch {
    return args;
  }
}

export function ToolCallArguments({
  args,
  toolCall,
}: {
  args?: Record<string, any> | string | null;
  toolCall: ToolCall;
}) {
  const normalized = normalizeArgs(args);

  if (typeof normalized === "string") {
    return (
      <div className="text-base">
        <Markdown>{normalized}</Markdown>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[auto_1fr] gap-2 text-base">
      {Object.entries<string>(normalized).map(([key, value], i) => {
        const formattedKey = titleCase(key.replace(/_/g, " "));

        if (!value) {
          return null;
        }

        if (Array.isArray(value) && value.length === 0) {
          return null;
        }

        if (key === "action_description") {
          return null;
        }

        if (Array.isArray(value)) {
          return (
            <React.Fragment key={`${key}:${i}`}>
              <span className="font-bold">{formattedKey}:</span>
              <div>
                {value.map((v, i) => (
                  <div key={i}>
                    {typeof v === "string" ? v : JSON.stringify(v)}
                  </div>
                ))}
              </div>
            </React.Fragment>
          );
        }

        if (typeof value === "object" && value !== null) {
          return (
            <React.Fragment key={`${key}:${i}`}>
              <span className="font-bold my-2">{formattedKey}:</span>
              <ToolCallArguments
                key={`${key}:${i}`}
                args={value}
                toolCall={toolCall}
              />
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={`${key}:${i}`}>
            <span className="font-bold my-2">{formattedKey}:</span>
            <span>
              <Markdown>
                {typeof value === "string" ? value : JSON.stringify(value)}
              </Markdown>
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}
